import AppIcon from '@atlaskit/icon/core/app';
import { Box, xcss } from '@atlaskit/primitives';
import { AccordionCollapsedEvent, AccordionExpandedEvent, ANALYTICS_ACTION_COLLAPSED, ANALYTICS_ACTION_EXPANDED, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { AccordionItemTriggerContent, AccordionV2 } from '@atlassian/mpac-primitives';
import { useScopesLookup } from '@atlassian/mpac-site-selection';
import { makeScopeKey } from '@atlassian/mpac-types';
import { Option } from 'funfix-core';
import { useEffect } from 'react';
import { AccordionError } from '../AccordionError/AccordionError';
import { AccordionLoading } from '../AccordionLoading/AccordionLoading';
import { ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_SCOPES_ACCORDION } from '../events';
import { INSTALLATION_BLOCKING_APIS } from '../useErrorAndLoadingStates';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const appScopesStyles = xcss({
  margin: 'space.0',
  gap: 'space.100',
  display: 'flex',
  flexDirection: 'column'
});
// eslint-disable-next-line max-lines-per-function
export const AppScopes = props => {
  const fireAnalyticsEvent = useAnalyticsEvents();
  const {
    appScopes: scopesList,
    graphQLGatewayUrl,
    updateErrAndLoadingState
  } = props;
  const {
    scopesLookup,
    pending
  } = useScopesLookup(graphQLGatewayUrl);

  // hasError is true even if the scopes call is pending, due to how the hook is implemented.
  const hasError = scopesLookup.isLeft();
  useEffect(() => {
    updateErrAndLoadingState === null || updateErrAndLoadingState === void 0 ? void 0 : updateErrAndLoadingState(INSTALLATION_BLOCKING_APIS.SCOPES, {
      // hasError && !pending implies scopes call was completed but returned an error.
      error: !!hasError && !pending,
      loading: pending
    });
  }, [hasError, pending, updateErrAndLoadingState]);
  const translateScope = _ref => {
    let {
      lookUp,
      scope
    } = _ref;
    const description = Option.of(lookUp.getIn([makeScopeKey(scope.toUpperCase()), 'description'])).orElseL(() => {
      const scopeSplit = scope.split(':');
      const scopeHalfKey = scopeSplit[scopeSplit.length - 1];
      return Option.of(lookUp.getIn([makeScopeKey(scopeHalfKey.toUpperCase()), 'description']));
    }).getOrElse(scope);
    return description;
  };
  const scopes = scopesLookup.fold(_ => scopesList.map(scope => /*#__PURE__*/_jsx(Box, {
    as: "li",
    children: scope
  }, scope)), lookUp => scopesList.map(scope => /*#__PURE__*/_jsx(Box, {
    as: "li",
    children: translateScope({
      lookUp,
      scope
    })
  }, scope)));
  if (pending) {
    return /*#__PURE__*/_jsx(AccordionLoading, {});
  }
  const handleExpandScope = id => {
    fireAnalyticsEvent({
      eventType: AccordionExpandedEvent,
      event: ANALYTICS_ACTION_EXPANDED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_SCOPES_ACCORDION,
      attributes: {
        scope: id
      }
    });
  };
  const handleCollapseScope = id => {
    fireAnalyticsEvent({
      eventType: AccordionCollapsedEvent,
      event: ANALYTICS_ACTION_COLLAPSED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_SCOPES_ACCORDION,
      attributes: {
        scope: id
      }
    });
  };
  const renderContent = () => {
    if (hasError) {
      return /*#__PURE__*/_jsx(AccordionError, {
        label: "App scopes error"
      });
    }
    if (props.appScopes.size > 0) return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        children: "This app will perform the following actions. It can also perform these actions on behalf of users:"
      }), /*#__PURE__*/_jsx(Box, {
        as: "ul",
        padding: "space.0",
        paddingInlineStart: "space.200",
        paddingInlineEnd: "space.600",
        paddingBlockStart: "space.100",
        xcss: appScopesStyles,
        children: scopes
      })]
    });
    return /*#__PURE__*/_jsx("div", {
      children: "This app does not require any permissions."
    });
  };
  return /*#__PURE__*/_jsx(AccordionV2, {
    mainTitle: "Permissions",
    onExpandItem: handleExpandScope,
    onCollapseItem: handleCollapseScope,
    items: [{
      heading: () => /*#__PURE__*/_jsx(AccordionItemTriggerContent, {
        count: !hasError ? scopes.size : undefined,
        title: "Actions",
        icon: /*#__PURE__*/_jsx(AppIcon, {
          label: "",
          color: "var(--ds-icon-disabled, #091E424F)"
        })
      }),
      content: renderContent,
      key: 'app-scopes',
      ariaLabel: 'Permission details'
    }]
  });
};